import React from 'react';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import useInertiaData from '@/hooks/useInertiaData';
import useRoutes from '@/hooks/useRoutes';
import { router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';

const DiscordConnection: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useInertiaData();
    const { route } = useRoutes();

    if (!user) {
        return null;
    }

    const isDiscordConnected = Boolean(user.discord_user_id);
    const discordStatusLabel = isDiscordConnected
        ? t('myAccount.discordConnectedAs', {
              username: user.discord_username || user.discord_user_id,
          })
        : t('myAccount.discordDisconnected');

    const handleConnect = () => {
        try {
            const url = route('discord-connect').toString();
            window.location.href = url;
        } catch (error) {
            console.error('Discord connect route is not available', error);
        }
    };

    const handleDisconnect = () => {
        const confirmed = window.confirm(
            t('myAccount.discordDisconnectConfirm') ||
                'Are you sure you want to disconnect your Discord account? You will be removed from the Discord server.'
        );

        if (confirmed) {
            try {
                router.post(route('discord-disconnect').toString());
            } catch (error) {
                console.error('Discord disconnect route is not available', error);
            }
        }
    };

    return (
        <div className="border-t border-gray-100 px-8 py-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">
                {t('myAccount.discordConnection')}
            </h3>

            <p className="mb-6 text-sm text-gray-600">
                {t('myAccount.discordConnectionDesc')}
            </p>

            <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <dt className="mb-1 text-sm font-medium text-gray-500">
                        {t('home.status')}
                    </dt>
                    <dd className="text-base">
                        <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${isDiscordConnected ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}
                        >
                            {discordStatusLabel}
                        </span>
                    </dd>
                </div>
                {isDiscordConnected ? (
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <SecondaryButton
                            type="button"
                            onClick={handleConnect}
                            size="md"
                        >
                            {t('myAccount.discordReconnectButton')}
                        </SecondaryButton>
                        <SecondaryButton
                            type="button"
                            onClick={handleDisconnect}
                            size="md"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                            {t('myAccount.discordDisconnectButton') ||
                                'Disconnect'}
                        </SecondaryButton>
                    </div>
                ) : (
                    <PrimaryButton
                        type="button"
                        onClick={handleConnect}
                        size="md"
                    >
                        {t('myAccount.discordConnectButton')}
                    </PrimaryButton>
                )}
            </div>

            <div className="mt-6 space-y-2 text-sm text-gray-600">
                <p>{t('myAccount.discordUpcomingFeatures')}</p>
                <ul className="list-disc space-y-2 pl-5">
                    <li>{t('myAccount.discordPendingRankSuffix')}</li>
                    <li>{t('myAccount.discordPendingChannelAccess')}</li>
                </ul>
            </div>
        </div>
    );
};

export default DiscordConnection;
